import React, { useEffect, useState } from 'react';
import Navbar from '../../components/invnavbar/invnavbar';
import Sidebar from '../../components/invsidebar/invsidebar';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

// Servicios y URLs
import {
    API_URLS,
    obtenerProductos,
    obtenerEntradas,
    obtenerSalidas,
    obtenerMantenimientos // Debes exportar esta función en tu servicio
} from '../../Services/servicioURL';

// Interfaces
interface InventarioItem {
    cve_producto: string;
    descripcion: string;
    modelo: string;
    entradaTotal: number;
    salidaTotal: number;
    mantenimientoTotal: number;
    existencia: number;
    fechaUltimaEntrada?: string;
}

interface EntradaDetalle {
    cve_producto: string;
    cantidad: number;
}

interface Entrada {
    folio: string;
    fecha: string;
    entradaDetalle: EntradaDetalle[];
}

interface SalidaDetalle {
    cve_producto: string;
    cantidad: number;
}

interface Salida {
    folio: string;
    salidaDetalle: SalidaDetalle[];
}

interface MantenimientoDetalle {
    cve_producto: string;
    cantidad: number;
    // ...otros campos si los hay
}

interface Mantenimiento {
    folio: string;
    mantenimientoDetalle: MantenimientoDetalle[];
    // ...otros campos si los hay
}

const Inventario: React.FC = () => {
    const [inventario, setInventario] = useState<InventarioItem[]>([]);
    const [filteredInventario, setFilteredInventario] = useState<InventarioItem[]>([]);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(true);
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | null>(null);

    // Función para obtener la fecha de la última entrada de un producto (modificada)
    const obtenerFechaUltimaEntrada = (cve_producto: string, entradas: Entrada[]): string | null => {
        const entradasDelProducto = entradas.filter(entrada => 
            entrada.entradaDetalle.some(detalle => detalle.cve_producto === cve_producto)
        );
        
        if (entradasDelProducto.length === 0) return null;
        
        // Ordenar por fecha descendente (usando comparación de strings)
        const entradasOrdenadas = entradasDelProducto.sort((a, b) => 
            b.fecha.localeCompare(a.fecha)
        );
        
        return entradasOrdenadas[0].fecha;
    };

    // Función para formatear fecha (modificada para evitar problemas de zona horaria)
    const formatearFecha = (fecha: string | null): string => {
        if (!fecha) return 'Sin entradas';
        
        try {
            // Extraer directamente las partes de la fecha sin conversión a Date
            const [year, month, day] = fecha.split('T')[0].split('-');
            return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`;
        } catch (error) {
            console.error('Error al formatear fecha:', error);
            return 'Fecha inválida';
        }
    };

    // Función para exportar a Excel
    const exportToExcel = () => {
        try {
            // Preparar los datos para Excel
            const excelData = filteredInventario.map(item => ({
                'Clave': item.cve_producto,
                'Modelo': item.modelo || 'N/A',
                'Descripción': item.descripcion,
                'Total Entradas': item.entradaTotal,
                'Total Salidas': item.salidaTotal,
                'Total Mantenimientos': item.mantenimientoTotal,
                'Existencia': item.existencia,
                'Última Entrada': formatearFecha(item.fechaUltimaEntrada || null)
            }));

            // Crear un nuevo libro de trabajo
            const wb = XLSX.utils.book_new();
            
            // Crear la hoja de trabajo con los datos
            const ws = XLSX.utils.json_to_sheet(excelData);

            // Configurar el ancho de las columnas
            const colWidths = [
                { wch: 12 }, // Clave
                { wch: 15 }, // Modelo
                { wch: 40 }, // Descripción
                { wch: 15 }, // Total Entradas
                { wch: 15 }, // Total Salidas
                { wch: 18 }, // Total Mantenimientos
                { wch: 12 }, // Existencia
                { wch: 18 }  // Última Entrada
            ];
            ws['!cols'] = colWidths;

            // Agregar la hoja al libro
            XLSX.utils.book_append_sheet(wb, ws, 'Inventario');

            // Generar el nombre del archivo con fecha actual
            const fecha = new Date().toISOString().slice(0, 10);
            const nombreArchivo = `inventario_BBT_SIS_${fecha}.xlsx`;

            // Descargar el archivo
            XLSX.writeFile(wb, nombreArchivo);

        } catch (error) {
            console.error('Error al generar Excel:', error);
            alert('Ocurrió un error al generar el archivo Excel. Por favor intente nuevamente.');
        }
    };

    // Función para exportar a PDF (simplificada)
    const exportToPDF = () => {
        try {
            const doc = new jsPDF('l', 'pt', 'a4');
            const img = new Image();
            img.src = '/dist/img/bbtt2.png';
            img.onload = function() {
                doc.addImage(img, 'PNG', 40, 20, 65, 65);
                doc.setProperties({
                    title: 'Reporte de Inventario BBT-SIS',
                    subject: 'Inventario actual',
                    author: 'Sistema de Inventarios',
                    keywords: 'inventario, reporte, pdf',
                    creator: 'Sistema de Inventarios'
                });

                doc.setFontSize(18);
                doc.setTextColor(40);
                doc.text('Reporte de Inventario BBT-SIS', 150, 40);
                doc.setFontSize(10);
                doc.setTextColor(100);
                doc.text(`Generado el: ${new Date().toLocaleDateString('es-ES')}`, 150, 60);
                doc.text(`Total de productos: ${filteredInventario.length}`, 150, 75);

                // Incluye mantenimientos en la tabla
                const tableData = filteredInventario.map(item => [
                    item.cve_producto,
                    item.modelo || 'N/A',
                    item.descripcion,
                    item.entradaTotal,
                    item.salidaTotal,
                    item.mantenimientoTotal,
                    item.existencia,
                    formatearFecha(item.fechaUltimaEntrada || null)
                ]);

                autoTable(doc, {
                    head: [['Clave', 'Modelo', 'Descripción', 'Entradas', 'Salidas', 'Mantenimientos', 'Existencia', 'Última Entrada']],
                    body: tableData,
                    startY: 90,
                    margin: { horizontal: 40 },
                    styles: {
                        fontSize: 8,
                        cellPadding: 3,
                        overflow: 'linebreak',
                        valign: 'middle'
                    },
                    headStyles: {
                        fillColor: [41, 128, 185],
                        textColor: 255,
                        fontStyle: 'bold',
                        halign: 'center',
                        fontSize: 9
                    },
                    bodyStyles: {
                        halign: 'center',
                        valign: 'middle'
                    },
                    columnStyles: {
                        0: { cellWidth: 60, halign: 'center' },
                        1: { cellWidth: 80, halign: 'center' },
                        2: { cellWidth: 'auto', halign: 'left' },
                        3: { cellWidth: 60, halign: 'center' },
                        4: { cellWidth: 60, halign: 'center' },
                        5: { cellWidth: 80, halign: 'center' },
                        6: { cellWidth: 60, halign: 'center' },
                        7: { cellWidth: 80, halign: 'center' }
                    },
                    didDrawPage: function (data: { pageNumber: any; settings: { margin: { left: number; }; }; }) {
                        const pageCount = doc.getNumberOfPages();
                        const currentPage = data.pageNumber;
                        doc.setFontSize(10);
                        doc.setTextColor(150);
                        doc.text(
                            `Página ${currentPage} de ${pageCount}`,
                            data.settings.margin.left,
                            doc.internal.pageSize.height - 20
                        );
                    }
                });

                doc.save(`inventario_${new Date().toISOString().slice(0, 10)}.pdf`);
            };
            img.onerror = function() {
                console.error('Error al cargar la imagen del membretado');
                doc.save(`inventario_${new Date().toISOString().slice(0, 10)}.pdf`);
            };
        } catch (error) {
            console.error('Error al generar PDF:', error);
            alert('Ocurrió un error al generar el PDF. Por favor intente nuevamente.');
        }
    };

    const sumarMovimientosPorProducto = (
        movimientos: (Entrada | Salida)[]
    ): Map<string, number> => {
        const totalMap = new Map<string, number>();

        movimientos.forEach(mov => {
            const detalles = 'entradaDetalle' in mov ? mov.entradaDetalle : mov.salidaDetalle;

            detalles.forEach(detalle => {
                const actual = totalMap.get(detalle.cve_producto) || 0;
                totalMap.set(detalle.cve_producto, actual + detalle.cantidad);
            });
        });

        return totalMap;
    };

    // NUEVO: sumar mantenimientos por producto
    const sumarMantenimientosPorProducto = (mantenimientos: Mantenimiento[]): Map<string, number> => {
        const totalMap = new Map<string, number>();
        mantenimientos.forEach(mant => {
            if (Array.isArray(mant.mantenimientoDetalle)) {
                mant.mantenimientoDetalle.forEach(detalle => {
                    const actual = totalMap.get(detalle.cve_producto) || 0;
                    totalMap.set(detalle.cve_producto, actual + detalle.cantidad);
                });
            }
        });
        return totalMap;
    };

    const sortByExistencia = (order: 'asc' | 'desc') => {
        const sorted = [...filteredInventario].sort((a, b) => {
            return order === 'asc' ? a.existencia - b.existencia : b.existencia - a.existencia;
        });
        setFilteredInventario(sorted);
        setSortOrder(order);
    };

    const resetOrder = () => {
        applyFilters();
        setSortOrder(null);
    };

    const applyFilters = () => {
        if (!searchTerm.trim()) {
            setFilteredInventario(inventario);
        } else {
            const term = searchTerm.toLowerCase();
            const filtered = inventario.filter(item =>
                item.descripcion.toLowerCase().includes(term) ||
                item.cve_producto.toLowerCase().includes(term) ||
                item.modelo.toLowerCase().includes(term)
            );
            setFilteredInventario(filtered);
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                const productos = await obtenerProductos(API_URLS.productos.obtener);
                const entradas = await obtenerEntradas(API_URLS.entradas.obtener);
                const salidas = await obtenerSalidas(API_URLS.salidas.obtener);
                const mantenimientos = await obtenerMantenimientos(API_URLS.mantenimientos.obtener);

                const entradasTotales = sumarMovimientosPorProducto(entradas);
                const salidasTotales = sumarMovimientosPorProducto(salidas);
                const mantenimientosTotales = sumarMantenimientosPorProducto(mantenimientos);

                const inventarioFinal = productos.map((producto: any) => {
                    const entradaTotal = entradasTotales.get(producto.cve) || 0;
                    const salidaTotal = salidasTotales.get(producto.cve) || 0;
                    const mantenimientoTotal = mantenimientosTotales.get(producto.cve) || 0;
                    const fechaUltimaEntrada = obtenerFechaUltimaEntrada(producto.cve, entradas);

                    return {
                        cve_producto: producto.cve,
                        descripcion: producto.descripcion,
                        modelo: producto.modelo,
                        entradaTotal,
                        salidaTotal,
                        mantenimientoTotal,
                        existencia: entradaTotal - salidaTotal - mantenimientoTotal,
                        fechaUltimaEntrada
                    };
                });

                setInventario(inventarioFinal);
                setFilteredInventario(inventarioFinal);

            } catch (error) {
                console.error('Error al cargar inventario:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    useEffect(() => {
        applyFilters();
        setSortOrder(null);
    }, [searchTerm, inventario]);

    return (
        <div>
            <Navbar />
            <Sidebar />

            <div className="content-wrapper" style={{
                backgroundImage: 'url(/dist/img/FondoLogin.png)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundAttachment: 'fixed',
                minHeight: '100vh',
                position: 'relative'
            }}>
                
                <div className="content-header">
                    <div className="container-fluid">
                        <div className="row mb-2">
                            <div className="col-sm-6">
                                <h1 className="m-0">Inventario</h1>
                            </div>
                            <div className="col-sm-6">
                                <ol className="breadcrumb float-sm-right">
                                    <li className="breadcrumb-item active">Inventario</li>
                                </ol>
                            </div>
                        </div>
                    </div>
                </div>

                <section className="content">
                    <div className="container-fluid">
                        <div className="row">
                            <div className="col-12">
                                <div className="card card-primary card-outline">
                                    <div className="card-body">
                                        <div style={{ 
                                            marginBottom: '1rem', 
                                            display: 'flex', 
                                            gap: '1rem', 
                                            alignItems: 'center',
                                            flexWrap: 'wrap'
                                        }}>
                                            <div>
                                                <InputText
                                                    value={searchTerm}
                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                    placeholder="Buscar producto..."
                                                    style={{ width: '300px' }}
                                                />
                                            </div>

                                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                                <span style={{ fontSize: '0.9rem', fontWeight: 'bold' }}>
                                                    Ordenar por existencia:
                                                </span>
                                                <Button
                                                    label="Mayor a Menor"
                                                    icon="pi pi-sort-amount-down"
                                                    size="small"
                                                    className={sortOrder === 'desc' ? 'p-button-success' : 'p-button-outlined'}
                                                    onClick={() => sortByExistencia('desc')}
                                                />
                                                <Button
                                                    label="Menor a Mayor"
                                                    icon="pi pi-sort-amount-up"
                                                    size="small"
                                                    className={sortOrder === 'asc' ? 'p-button-success' : 'p-button-outlined'}
                                                    onClick={() => sortByExistencia('asc')}
                                                />
                                                <Button
                                                    label="Sin orden"
                                                    icon="pi pi-times"
                                                    size="small"
                                                    className={sortOrder === null ? 'p-button-secondary' : 'p-button-outlined'}
                                                    onClick={resetOrder}
                                                />
                                            </div>

                                            <div style={{ marginLeft: 'auto', display: 'flex', gap: '1rem' }}>
                                                <Button
                                                    label=""
                                                    icon="pi pi-file-excel"
                                                    severity="success"
                                                    onClick={exportToExcel}
                                                    size="large"
                                                    tooltip="Generar reporte en Excel"
                                                    tooltipOptions={{ position: 'top' }}
                                                    disabled={filteredInventario.length === 0}
                                                />
                                                <Button
                                                    label=""
                                                    icon="pi pi-file-pdf"
                                                    severity="danger"
                                                    onClick={exportToPDF}
                                                    size="large"
                                                    tooltip="Generar reporte en PDF"
                                                    tooltipOptions={{ position: 'top' }}
                                                    disabled={filteredInventario.length === 0}
                                                />
                                            </div>
                                        </div>

                                        {sortOrder && (
                                            <div style={{ 
                                                marginBottom: '1rem', 
                                                padding: '0.5rem', 
                                                backgroundColor: '#e8f5e8', 
                                                borderRadius: '4px',
                                                fontSize: '0.9rem'
                                            }}>
                                                <i className="pi pi-info-circle" style={{ marginRight: '0.5rem' }}></i>
                                                Ordenado por existencia: {sortOrder === 'desc' ? 'Mayor a menor' : 'Menor a mayor'}
                                            </div>
                                        )}

                                        <DataTable
                                            value={filteredInventario}
                                            responsiveLayout="scroll"
                                            stripedRows
                                            size="small"
                                            emptyMessage={loading ? 'Cargando inventario...' : 'No hay productos en inventario'}
                                            className="p-datatable-sm"
                                            style={{ fontSize: '0.9rem' }}
                                            paginator
                                            rows={10}
                                            rowsPerPageOptions={[5, 10, 25, 50]}
                                            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                                            currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} productos"
                                        >
                                            <Column
                                                field="cve_producto"
                                                header="Clave"
                                                headerStyle={{ fontWeight: 'bold', textAlign: 'center' }}
                                                style={{ width: '80px', textAlign: 'center' }}
                                            />
                                            <Column
                                                field="modelo"
                                                header="Modelo"
                                                headerStyle={{ fontWeight: 'bold', textAlign: 'center' }}
                                                style={{ width: '100px', textAlign: 'center' }}
                                            />
                                            <Column
                                                field="descripcion"
                                                header="Descripción"
                                                headerStyle={{ fontWeight: 'bold' }}
                                            />
                                            <Column
                                                field="entradaTotal"
                                                header="Entradas"
                                                headerStyle={{ fontWeight: 'bold', textAlign: 'center' }}
                                                style={{ width: '100px', textAlign: 'center' }}
                                            />
                                            <Column
                                                field="salidaTotal"
                                                header="Salidas"
                                                headerStyle={{ fontWeight: 'bold', textAlign: 'center' }}
                                                style={{ width: '100px', textAlign: 'center' }}
                                            />
                                            <Column
                                                field="mantenimientoTotal"
                                                header="Salidas por mantenimiento"
                                                headerStyle={{ fontWeight: 'bold', textAlign: 'center' }}
                                                style={{ width: '120px', textAlign: 'center' }}
                                            />
                                            <Column
                                                field="existencia"
                                                header="Cantidad"
                                                body={rowData => (
                                                    <span style={{ 
                                                        fontWeight: 'bold',
                                                        color: rowData.existencia <= 0 ? '#dc3545' : 
                                                               rowData.existencia <= 5 ? '#fd7e14' : '#28a745'
                                                    }}>
                                                        {rowData.existencia}
                                                    </span>
                                                )}
                                                headerStyle={{ width: '100px', textAlign: 'center', fontWeight: 'bold' }}
                                                style={{ textAlign: 'center' }}
                                            />
                                            <Column
                                                field="fechaUltimaEntrada"
                                                header="Última Entrada"
                                                body={rowData => (
                                                    <span style={{ 
                                                        fontSize: '0.85rem',
                                                        color: rowData.fechaUltimaEntrada ? '#495057' : '#6c757d',
                                                        fontStyle: rowData.fechaUltimaEntrada ? 'normal' : 'italic'
                                                    }}>
                                                        {formatearFecha(rowData.fechaUltimaEntrada || null)}
                                                    </span>
                                                )}
                                                headerStyle={{ width: '120px', textAlign: 'center', fontWeight: 'bold' }}
                                                style={{ textAlign: 'center' }}
                                            />
                                        </DataTable>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </div>
    );
};

export default Inventario;